import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      // Common/General terms
      common: {
        "email": "Email",
        "password": "Password",
        "confirmPassword": "Confirm Password",
        "yes": "Yes",
        "no": "No",
        "unknown": "Unknown",
        "sending": "Sending...",
        "language": "Language",
        "english": "English",
        "dutch": "Nederlands",
        "cancel": "Cancel",
        "user": "user",
        "users": "users",
        "quest_one": "Quest",
        "quest_other": "Quests"
      },
      
      // Login page
      loginPage: {
        "welcome": "Welcome to StreamQuest",
        "twitchLogin": "Sign in with your Twitch account to get started",
        "loginWithTwitch": "Login with Twitch",
        "connectingToTwitch": "Connecting to Twitch...",
        "twitchInfo": "We'll redirect you to Twitch to authorize your account",
        "logIn": "Log In",
        "loggingIn": "Logging In...",
        "failedToLogin": "Failed to log in",
        "forgotPassword": "Forgot Password?",
        "needAccount": "Need an account?",
        "signUp": "Sign Up",
        "mustAcceptTerms": "You must accept the terms and conditions to continue",
        "acceptTerms": {
          "text": "I accept the",
          "termsLink": "Terms and Conditions",
          "and": "and",
          "privacyLink": "Privacy Policy"
        }
      },
      
      // Register page  
      registerPage: {
        "signUp": "Sign Up",
        "creatingAccount": "Creating Account...",
        "failedToCreateAccount": "Failed to create an account",
        "passwordsDoNotMatch": "Passwords do not match",
        "passwordMinLength": "Password must be at least 6 characters",
        "alreadyHaveAccount": "Already have an account?",
        "logIn": "Log In"
      },
      
      // Forgot Password page
      forgotPasswordPage: {
        "passwordReset": "Password Reset",
        "resetPassword": "Reset Password",
        "checkInbox": "Check your inbox for further instructions",
        "failedToResetPassword": "Failed to reset password",
        "backToLogin": "Back to Login",
        "needAccount": "Need an account?",
        "signUp": "Sign Up"
      },
      
      // Dashboard page
      dashboard: {
        "title": "Dashboard",
        "logout": "Log Out",
        "welcomeBack": "Welcome back",
        "failedToLogout": "Failed to log out",
        "failedToStartQuest": "Failed to start quest",
        "failedToCompleteStep": "Failed to complete step",
        "failedToAbandonQuest": "Failed to abandon quest",
        "stats": {
          "questsCompleted": "Quests Completed",
          "totalXP": "Total XP",
          "streakDays": "Day Streak",
          "activeQuests": "Active Quests"
        }
      },

      // Authentication callbacks
      auth: {
        "twitch": {
          "processing": "Authenticating with Twitch...",
          "processingDescription": "Please wait while we verify your account",
          "success": "Authentication Successful!",
          "successDescription": "Redirecting you to your dashboard...",
          "error": "Authentication Failed",
          "errorDescription": "Redirecting you back to login..."
        }
      },

      // Profile component
      profile: {
        "anonymousUser": "Anonymous User",
        "memberSince": "Member Since",
        "lastActive": "Last Active",
        "emailStatus": "Email Status",
        "verified": "Verified",
        "unverified": "Not Verified",
        "achievements": "Achievements",
        "badges": {
          "newUser": "New User",
          "verified": "Email Verified"
        }
      },

      // Timeline component
      timeline: {
        "title": "Activity Timeline",
        "recentActivity": "Recent Activity",
        "noActivity": "No recent activity to display",
        "events": {
          "accountCreated": "Account Created",
          "profileUpdated": "Profile Updated",
          "questCompleted": "Quest Completed",
          "completedFirstQuest": "Completed your first quest!",
          "achievementUnlocked": "Achievement Unlocked",
          "newUserBadge": "Earned the New User badge"
        }
      },

      // Admin system
      admin: {
        "portal": "Admin Portal",
        "accessDenied": "Access Denied",
        "noPermission": "You don't have permission to access the admin portal.",
        "pleaseLogin": "Please log in first.",
        "checkingPrivileges": "Checking admin privileges...",
        "overview": "Overview",
        "quests": "Quests",
        "questManagement": "Quest Management",
        "userManagement": "User Management",
        "searchQuests": "Search quests...",
        "searchUsers": "Search users...",
        "createQuest": "Create Quest",
        "createNewQuest": "Create New Quest",
        "editQuest": "Edit Quest",
        "deleteQuest": "Delete Quest",
        "confirmDelete": "Are you sure you want to delete this quest?",
        "selectQuest": "Select a quest to view details",
        "loadingQuests": "Loading quests...",
        "loadingUsers": "Loading users...",
        "questCreated": "Quest created successfully",
        "questUpdated": "Quest updated successfully",
        "questDeleted": "Quest deleted successfully",
        "failedToCreate": "Failed to create quest",
        "failedToUpdate": "Failed to update quest",
        "failedToDelete": "Failed to delete quest",
        "form": {
          "title": "Title",
          "titlePlaceholder": "Enter quest title",
          "description": "Description",
          "descriptionPlaceholder": "Describe the quest",
          "xpValue": "XP Value",
          "difficulty": "Difficulty",
          "category": "Category",
          "categoryPlaceholder": "e.g., fitness, learning",
          "timeEstimate": "Time Estimate",
          "timeEstimatePlaceholder": "e.g., 30 min, 1 hour",
          "translations": "Translations",
          "addTranslation": "Add Translation",
          "language": "Language",
          "selectLanguage": "Select language",
          "saveChanges": "Save Changes",
          "cancel": "Cancel",
          "close": "Close"
        },
        "status": {
          "active": "Active",
          "inactive": "Inactive"
        },
        "stats": {
          "overviewAndStatistics": "Overview & Statistics",
          "userStatistics": "User Statistics",
          "totalUsers": "Total Users",
          "activeUsers": "Active Users",
          "totalXPEarned": "Total XP Earned",
          "avgXPPerUser": "Avg XP per User",
          "questActivity": "Quest Activity",
          "questsCompleted": "Quests Completed",
          "questsFailed": "Quests Failed",
          "activeQuests": "Active Quests",
          "overallSuccessRate": "Overall Success Rate",
          "availableQuests": "Available Quests",
          "totalQuests": "Total Quests",
          "inactiveQuests": "Inactive Quests",
          "avgXPReward": "Avg XP Reward",
          "questBreakdown": "Quest Breakdown",
          "byDifficulty": "By Difficulty",
          "byCategory": "By Category",
          "loadingStatistics": "Loading statistics..."
        },
        "users": {
          "email": "E-mail",
          "basicInformation": "Basic Information",
          "username": "Username",
          "created": "Created",
          "lastActive": "Last Active",
          "updated": "Updated",
          "questStatistics": "Quest Statistics",
          "completed": "Completed",
          "failed": "Failed",
          "active": "Active",
          "totalXP": "Total XP",
          "successRate": "Success Rate",
          "questHistoryPreview": "Quest History Preview",
          "noUsers": "No Users Found",
          "tryDifferentSearch": "Try adjusting your search terms",
          "noSelection": "Select a user to view details",
          "notSet": "Not set",
          "never": "Never",
          "anonymousUser": "Anonymous User",
          "loadingUsers": "Loading users...",
          "searchByEmail": "Search by email...",
          "searchByUsername": "Search by username...",
          "searchByUID": "Search by UID...",
          "searchType": "Search type",
          "searchTypeEmail": "Email",
          "searchTypeUsername": "Username", 
          "searchTypeUID": "User ID",
          "search": "Search",
          "found": "found",
          "userNotFound": "User not found",
          "searchError": "Search Error",
          "noUsersFound": "No users found",
          "tryDifferentEmail": "Try a different email address",
          "searchPrompt": "Search for a User",
          "enterEmailToSearch": "Enter an email address to search for a user",
          "xpHistory": "XP History",
          "addXPEntry": "Add XP Entry",
          "noXPHistory": "No XP history found",
          "deleteXPEntry": "Delete XP Entry",
          "confirmDeleteXP": "Are you sure you want to delete this XP entry?",
          "addXPError": "Failed to add XP entry",
          "deleteXPError": "Failed to delete XP entry",
          "xpEntryTitle": "Title",
          "xpEntryTitlePlaceholder": "Enter XP entry title",
          "xpValue": "XP Value",
          "xpEntryType": "Type",
          "xpEntryStatus": "Status",
          "typeQuest": "Quest",
          "typeBonus": "Bonus",
          "typeAchievement": "Achievement",
          "statusCompleted": "Completed",
          "statusFailed": "Failed",
          "statusActive": "Active"
        },
        "questApproval": {
          "title": "Quest Approval",
          "pendingRequests": "Pending Requests",
          "approvedRequests": "Approved Requests",
          "rejectedRequests": "Rejected Requests",
          "noPendingRequests": "No pending quest requests",
          "approveRequest": "Approve Request",
          "rejectRequest": "Reject Request",
          "viewDetails": "View Details",
          "requestDetails": "Request Details",
          "requestedBy": "Requested by",
          "requestedAt": "Requested at",
          "questTitle": "Quest Title",
          "questDescription": "Quest Description",
          "questXP": "XP Value",
          "questDifficulty": "Difficulty",
          "questCategory": "Category",
          "approvalMessage": "Approval Message",
          "rejectionMessage": "Rejection Message",
          "approvalMessagePlaceholder": "Optional message for the user",
          "rejectionMessagePlaceholder": "Reason for rejection",
          "confirmApproval": "Are you sure you want to approve this quest request?",
          "confirmRejection": "Are you sure you want to reject this quest request?",
          "requestApproved": "Request approved successfully",
          "requestRejected": "Request rejected successfully",
          "failedToApprove": "Failed to approve request",
          "failedToReject": "Failed to reject request",
          "loadingRequests": "Loading requests...",
          "pending": "Pending",
          "approved": "Approved",
          "rejected": "Rejected"
        }
      },

      // Quest system
      quests: {
        "available": "available",
        "startQuest": "Start Quest",
        "requestApproval": "Request Approval",
        "abandon": "Abandon Quest",
        "progress": "Progress",
        "onCompletion": "on completion",
        "description": "Description",
        "subquests": "Sub-quests",
        "difficulty": {
          "easy": "Easy",
          "medium": "Medium",
          "hard": "Hard"
        },
        "status": {
          "completed": "Completed",
          "failed": "Failed",
          "active": "Active",
          "pending": "Pending Approval",
          "approved": "Approved",
          "rejected": "Rejected"
        },
        "approval": {
          "title": "Quest Approval",
          "pending": "Pending",
          "approved": "Approved",
          "rejected": "Rejected",
          "requestSent": "Request sent for approval",
          "waitingForApproval": "Waiting for admin approval",
          "approvalPending": "Your quest request is pending approval",
          "approvalApproved": "Your quest request has been approved! You can now start the quest.",
          "approvalRejected": "Your quest request has been rejected.",
          "approveRequest": "Approve Request",
          "rejectRequest": "Reject Request",
          "viewRequest": "View Request",
          "noRequestsFound": "No pending requests found",
          "requestDetails": "Request Details",
          "requestedBy": "Requested by",
          "requestedAt": "Requested at",
          "approvalRequired": "This quest requires admin approval before starting"
        },
        "pending": {
          "title": "Available Quests",
          "noPendingQuests": "No quests available at the moment",
          "watchTutorials": "Watch Tutorial Videos",
          "watchTutorialsDesc": "Complete the introductory tutorial series",
          "completeProfile": "Complete Your Profile",
          "completeProfileDesc": "Fill out all required profile information",
          "shareContent": "Share Your First Content",
          "shareContentDesc": "Create and share your first piece of content"
        },
        "active": {
          "title": "Active Quest",
          "inProgress": "In Progress",
          "noActiveQuest": "No Active Quest",
          "startNewQuest": "Start a new quest from the available quests list",
          "verifyEmail": "Verify Your Email",
          "verifyEmailDesc": "Complete the email verification process",
          "started": "Started",
          "remaining": "remaining"
        },
        "steps": {
          "title": "Quest Steps",
          "complete": "Complete",
          "checkEmail": "Check your email inbox",
          "clickLink": "Click the verification link",
          "confirmVerification": "Confirm verification"
        },
        "history": {
          "title": "Quest History",
          "all": "All",
          "completed": "Completed",
          "failed": "Failed",
          "noHistory": "No Quest History",
          "startCompleting": "Start completing quests to see your history here",
          "missed": "missed",
          "firstLogin": "First Login",
          "firstLoginDesc": "Successfully logged in for the first time",
          "profileSetup": "Profile Setup",
          "profileSetupDesc": "Completed initial profile setup",
          "socialShare": "Social Media Share",
          "socialShareDesc": "Share content on social media platforms",
          "weeklyChallenge": "Weekly Challenge",
          "weeklyChallengeDesc": "Complete the weekly challenge quest"
        }
      }
    }
  },
  tr: {
    translation: {
      // Common/General terms
      common: {
        "email": "E-posta",
        "password": "Şifre",
        "confirmPassword": "Şifreyi Onayla",
        "yes": "Evet",
        "no": "Hayır",
        "unknown": "Bilinmiyor",
        "sending": "Gönderiliyor...",
        "language": "Dil",
        "english": "English",
        "dutch": "Nederlands",
        "cancel": "İptal",
        "user": "kullanıcı",
        "users": "kullanıcı",
        "quest_one": "Görev",
        "quest_other": "Görev"
      },
      
      // Login page
      loginPage: {
        "welcome": "StreamQuest'e Hoş Geldiniz",
        "twitchLogin": "Başlamak için Twitch hesabınızla giriş yapın",
        "loginWithTwitch": "Twitch ile Giriş Yap",
        "connectingToTwitch": "Twitch'e bağlanılıyor...",
        "twitchInfo": "Hesabınızı yetkilendirmek için sizi Twitch'e yönlendireceğiz",
        "logIn": "Giriş Yap",
        "loggingIn": "Giriş Yapılıyor...",
        "failedToLogin": "Giriş başarısız",
        "forgotPassword": "Şifremi Unuttum?",
        "needAccount": "Hesabınız yok mu?",
        "signUp": "Kayıt Ol",
        "mustAcceptTerms": "Devam etmek için şartlar ve koşulları kabul etmelisiniz",
        "acceptTerms": {
          "text": "Kabul ediyorum",
          "termsLink": "Şartlar ve Koşullar",
          "and": "ve",
          "privacyLink": "Gizlilik Politikası"
        }
      },
      
      // Register page  
      registerPage: {
        "signUp": "Kayıt Ol",
        "creatingAccount": "Hesap Oluşturuluyor...",
        "failedToCreateAccount": "Hesap oluşturulamadı",
        "passwordsDoNotMatch": "Şifreler eşleşmiyor",
        "passwordMinLength": "Şifre en az 6 karakter olmalıdır",
        "alreadyHaveAccount": "Zaten hesabınız var mı?",
        "logIn": "Giriş Yap"
      },
      
      // Forgot Password page
      forgotPasswordPage: {
        "passwordReset": "Şifre Sıfırlama",
        "resetPassword": "Şifreyi Sıfırla",
        "checkInbox": "Talimatlar için e-posta kutunuzu kontrol edin",
        "failedToResetPassword": "Şifre sıfırlama başarısız",
        "backToLogin": "Girişe Dön",
        "needAccount": "Hesabınız yok mu?",
        "signUp": "Kayıt Ol"
      },
      
      // Dashboard page
      dashboard: {
        "title": "Ana Sayfa",
        "logout": "Çıkış Yap",
        "welcomeBack": "Tekrar hoş geldiniz",
        "failedToLogout": "Çıkış yapılamadı",
        "failedToStartQuest": "Görev başlatılamadı",
        "failedToCompleteStep": "Adım tamamlanamadı",
        "failedToAbandonQuest": "Görev bırakılamadı",
        "stats": {
          "questsCompleted": "Tamamlanan Görevler",
          "totalXP": "Toplam XP",
          "streakDays": "Gün Serisi",
          "activeQuests": "Aktif Görevler"
        }
      },

      // Authentication callbacks
      auth: {
        "twitch": {
          "processing": "Twitch ile kimlik doğrulanıyor...",
          "processingDescription": "Hesabınızı doğrularken lütfen bekleyin",
          "success": "Kimlik Doğrulama Başarılı!",
          "successDescription": "Ana sayfaya yönlendiriliyorsunuz...",
          "error": "Kimlik Doğrulama Başarısız",
          "errorDescription": "Giriş sayfasına yönlendiriliyorsunuz..."
        }
      },

      // Profile component
      profile: {
        "anonymousUser": "Anonim Kullanıcı",
        "memberSince": "Üyelik Tarihi",
        "lastActive": "Son Aktivite",
        "emailStatus": "E-posta Durumu",
        "verified": "Doğrulandı",
        "unverified": "Doğrulanmadı",
        "achievements": "Başarımlar",
        "badges": {
          "newUser": "Yeni Kullanıcı",
          "verified": "E-posta Doğrulandı"
        }
      },

      // Timeline component
      timeline: {
        "title": "Aktivite Zaman Çizelgesi",
        "recentActivity": "Son Aktiviteler",
        "noActivity": "Görüntülenecek son aktivite yok",
        "events": {
          "accountCreated": "Hesap Oluşturuldu",
          "profileUpdated": "Profil Güncellendi",
          "questCompleted": "Görev Tamamlandı",
          "completedFirstQuest": "İlk görevinizi tamamladınız!",
          "achievementUnlocked": "Başarım Açıldı",
          "newUserBadge": "Yeni Kullanıcı rozeti kazandınız"
        }
      },

      // Admin system
      admin: {
        "portal": "Yönetici Paneli",
        "accessDenied": "Erişim Engellendi",
        "noPermission": "Yönetici paneline erişim izniniz yok.",
        "pleaseLogin": "Lütfen önce giriş yapın.",
        "checkingPrivileges": "Yönetici yetkileri kontrol ediliyor...",
        "overview": "Genel Bakış",
        "quests": "Görevler",
        "questManagement": "Görev Yönetimi",
        "userManagement": "Kullanıcı Yönetimi",
        "searchQuests": "Görev ara...",
        "searchUsers": "Kullanıcı ara...",
        "createQuest": "Görev Oluştur",
        "createNewQuest": "Yeni Görev Oluştur",
        "editQuest": "Görevi Düzenle",
        "deleteQuest": "Görevi Sil",
        "confirmDelete": "Bu görevi silmek istediğinizden emin misiniz?",
        "selectQuest": "Detayları görmek için bir görev seçin",
        "loadingQuests": "Görevler yükleniyor...",
        "loadingUsers": "Kullanıcılar yükleniyor...",
        "questCreated": "Görev başarıyla oluşturuldu",
        "questUpdated": "Görev başarıyla güncellendi",
        "questDeleted": "Görev başarıyla silindi",
        "failedToCreate": "Görev oluşturulamadı",
        "failedToUpdate": "Görev güncellenemedi",
        "failedToDelete": "Görev silinemedi",
        "form": {
          "title": "Başlık",
          "titlePlaceholder": "Görev başlığını girin",
          "description": "Açıklama",
          "descriptionPlaceholder": "Görevi açıklayın",
          "xpValue": "XP Değeri",
          "difficulty": "Zorluk",
          "category": "Kategori",
          "categoryPlaceholder": "ör. fitness, öğrenme",
          "timeEstimate": "Tahmini Süre",
          "timeEstimatePlaceholder": "ör. 30 dk, 1 saat",
          "translations": "Çeviriler",
          "addTranslation": "Çeviri Ekle",
          "language": "Dil",
          "selectLanguage": "Dil seçin",
          "saveChanges": "Değişiklikleri Kaydet",
          "cancel": "İptal",
          "close": "Kapat"
        },
        "status": {
          "active": "Aktif",
          "inactive": "Pasif"
        },
        "stats": {
          "overviewAndStatistics": "Genel Bakış ve İstatistikler",
          "userStatistics": "Kullanıcı İstatistikleri",
          "totalUsers": "Toplam Kullanıcı",
          "activeUsers": "Aktif Kullanıcılar",
          "totalXPEarned": "Kazanılan Toplam XP",
          "avgXPPerUser": "Kullanıcı Başına Ortalama XP",
          "questActivity": "Görev Aktivitesi",
          "questsCompleted": "Tamamlanan Görevler",
          "questsFailed": "Başarısız Görevler",
          "activeQuests": "Aktif Görevler",
          "overallSuccessRate": "Genel Başarı Oranı",
          "availableQuests": "Mevcut Görevler",
          "totalQuests": "Toplam Görev",
          "inactiveQuests": "Pasif Görevler",
          "avgXPReward": "Ortalama XP Ödülü",
          "questBreakdown": "Görev Dağılımı",
          "byDifficulty": "Zorluk Seviyesine Göre",
          "byCategory": "Kategoriye Göre",
          "loadingStatistics": "İstatistikler yükleniyor..."
        },
        "users": {
          "email": "E-posta",
          "basicInformation": "Temel Bilgiler",
          "username": "Kullanıcı Adı",
          "created": "Oluşturulma",
          "lastActive": "Son Aktiflik",
          "updated": "Güncelleme",
          "questStatistics": "Görev İstatistikleri",
          "completed": "Tamamlanan",
          "failed": "Başarısız",
          "active": "Aktif",
          "totalXP": "Toplam XP",
          "successRate": "Başarı Oranı",
          "questHistoryPreview": "Görev Geçmişi Önizlemesi",
          "noUsers": "Kullanıcı Bulunamadı",
          "tryDifferentSearch": "Arama terimlerinizi değiştirmeyi deneyin",
          "noSelection": "Detayları görmek için bir kullanıcı seçin",
          "notSet": "Ayarlanmamış",
          "never": "Hiçbir zaman",
          "anonymousUser": "Anonim Kullanıcı",
          "loadingUsers": "Kullanıcılar yükleniyor...",
          "searchByEmail": "E-posta ile ara...",
          "searchByUsername": "Kullanıcı adı ile ara...",
          "searchByUID": "Kullanıcı ID ile ara...",
          "searchType": "Arama türü",
          "searchTypeEmail": "E-posta",
          "searchTypeUsername": "Kullanıcı adı",
          "searchTypeUID": "Kullanıcı ID",
          "search": "Ara",
          "found": "bulundu",
          "userNotFound": "Kullanıcı bulunamadı",
          "searchError": "Arama Hatası",
          "noUsersFound": "Kullanıcı bulunamadı",
          "tryDifferentEmail": "Farklı bir e-posta adresi deneyin",
          "searchPrompt": "Kullanıcı Ara",
          "enterEmailToSearch": "Kullanıcı aramak için bir e-posta adresi girin"
        },
        "questApproval": {
          "title": "Görev Onayı",
          "pendingRequests": "Bekleyen Talepler",
          "approvedRequests": "Onaylanan Talepler",
          "rejectedRequests": "Reddedilen Talepler",
          "noPendingRequests": "Bekleyen görev talebi yok",
          "pending": "Bekliyor",
          "approved": "Onaylandı",
          "rejected": "Reddedildi",
          "approveRequest": "Talebi Onayla",
          "rejectRequest": "Talebi Reddet",
          "viewDetails": "Detayları Gör",
          "requestDetails": "Talep Detayları",
          "requestedBy": "Talep eden",
          "requestedAt": "Talep tarihi",
          "questTitle": "Görev Başlığı",
          "questDescription": "Görev Açıklaması",
          "questXP": "XP Değeri",
          "questDifficulty": "Zorluk",
          "questCategory": "Kategori",
          "approvalMessage": "Onay Mesajı",
          "rejectionMessage": "Reddetme Mesajı",
          "approvalMessagePlaceholder": "Kullanıcı için isteğe bağlı mesaj",
          "rejectionMessagePlaceholder": "Reddetme sebebi",
          "confirmApproval": "Bu görev talebini onaylamak istediğinizden emin misiniz?",
          "confirmRejection": "Bu görev talebini reddetmek istediğinizden emin misiniz?",
          "requestApproved": "Talep başarıyla onaylandı",
          "requestRejected": "Talep başarıyla reddedildi",
          "failedToApprove": "Talep onaylanamadı",
          "failedToReject": "Talep reddedilemedi",
          "loadingRequests": "Talepler yükleniyor..."
        }
      },

      // Quest system
      quests: {
        "available": "mevcut",
        "startQuest": "Görevi Başlat",
        "abandon": "Görevi Bırak",
        "progress": "İlerleme",
        "onCompletion": "tamamlamada",
        "description": "Açıklama",
        "subquests": "Alt Görevler",
        "difficulty": {
          "easy": "Kolay",
          "medium": "Orta",
          "hard": "Zor"
        },
        "status": {
          "completed": "Tamamlandı",
          "failed": "Başarısız",
          "active": "Aktif",
          "pending": "Onay Bekliyor",
          "approved": "Onaylandı",
          "rejected": "Reddedildi"
        },
        "pending": {
          "title": "Mevcut Görevler",
          "noPendingQuests": "Şu anda mevcut görev bulunmuyor",
          "watchTutorials": "Eğitim Videolarını İzle",
          "watchTutorialsDesc": "Giriş eğitim serisini tamamla",
          "completeProfile": "Profilinizi Tamamlayın",
          "completeProfileDesc": "Tüm gerekli profil bilgilerini doldurun",
          "shareContent": "İlk İçeriğinizi Paylaşın",
          "shareContentDesc": "İlk içeriğinizi oluşturun ve paylaşın"
        },
        "active": {
          "title": "Aktif Görev",
          "inProgress": "Devam Ediyor",
          "noActiveQuest": "Aktif Görev Yok",
          "startNewQuest": "Mevcut görevler listesinden yeni bir görev başlatın",
          "verifyEmail": "E-postanızı Doğrulayın",
          "verifyEmailDesc": "E-posta doğrulama sürecini tamamlayın",
          "started": "Başlandı",
          "remaining": "kalan"
        },
        "steps": {
          "title": "Görev Adımları",
          "complete": "Tamamla",
          "checkEmail": "E-posta gelen kutunuzu kontrol edin",
          "clickLink": "Doğrulama bağlantısına tıklayın",
          "confirmVerification": "Doğrulamayı onaylayın"
        },
        "history": {
          "title": "Görev Geçmişi",
          "all": "Tümü",
          "completed": "Tamamlandı",
          "failed": "Başarısız",
          "noHistory": "Görev Geçmişi Yok",
          "startCompleting": "Geçmişinizi burada görmek için görevleri tamamlamaya başlayın",
          "missed": "kaçırıldı",
          "firstLogin": "İlk Giriş",
          "firstLoginDesc": "İlk kez başarıyla giriş yapıldı",
          "profileSetup": "Profil Kurulumu",
          "profileSetupDesc": "İlk profil kurulumu tamamlandı",
          "socialShare": "Sosyal Medya Paylaşımı",
          "socialShareDesc": "Sosyal medya platformlarında içerik paylaşın",
          "weeklyChallenge": "Haftalık Mücadele",
          "weeklyChallengeDesc": "Haftalık mücadele görevini tamamlayın"
        }
      }
    }
  },
  nl: {
    translation: {
      // Common/General terms
      common: {
        "email": "E-mail",
        "password": "Wachtwoord",
        "confirmPassword": "Bevestig Wachtwoord",
        "yes": "Ja",
        "no": "Nee",
        "unknown": "Onbekend",
        "sending": "Verzenden...",
        "language": "Taal",
        "english": "English",
        "dutch": "Nederlands",
        "cancel": "Annuleren",
        "user": "gebruiker",
        "users": "gebruikers",
        "quest_one": "Quest",
        "quest_other": "Quests",
      },
      
      // Login page
      loginPage: {
        "welcome": "Welkom bij StreamQuest",
        "twitchLogin": "Log in met je Twitch-account om te beginnen",
        "loginWithTwitch": "Inloggen met Twitch",
        "connectingToTwitch": "Verbinden met Twitch...",
        "twitchInfo": "We leiden je door naar Twitch om je account te autoriseren",
        "logIn": "Inloggen",
        "loggingIn": "Inloggen...",
        "failedToLogin": "Inloggen mislukt",
        "forgotPassword": "Wachtwoord Vergeten?",
        "needAccount": "Heeft u een account nodig?",
        "signUp": "Registreren",
        "mustAcceptTerms": "U moet de algemene voorwaarden accepteren om door te gaan",
        "acceptTerms": {
          "text": "Ik accepteer de",
          "termsLink": "Algemene Voorwaarden",
          "and": "en",
          "privacyLink": "Privacybeleid"
        }
      },
      
      // Register page  
      registerPage: {
        "signUp": "Registreren",
        "creatingAccount": "Account Aanmaken...",
        "failedToCreateAccount": "Account aanmaken mislukt",
        "passwordsDoNotMatch": "Wachtwoorden komen niet overeen",
        "passwordMinLength": "Wachtwoord moet minimaal 6 tekens bevatten",
        "alreadyHaveAccount": "Heeft u al een account?",
        "logIn": "Inloggen"
      },
      
      // Forgot Password page
      forgotPasswordPage: {
        "passwordReset": "Wachtwoord Resetten",
        "resetPassword": "Reset Wachtwoord",
        "checkInbox": "Controleer uw inbox voor verdere instructies",
        "failedToResetPassword": "Wachtwoord resetten mislukt",
        "backToLogin": "Terug naar Inloggen",
        "needAccount": "Heeft u een account nodig?",
        "signUp": "Registreren"
      },
      
      // Dashboard page
      dashboard: {
        "title": "Dashboard",
        "logout": "Uitloggen",
        "welcomeBack": "Welkom terug",
        "failedToLogout": "Uitloggen mislukt",
        "failedToStartQuest": "Quest starten mislukt",
        "failedToCompleteStep": "Stap voltooien mislukt",
        "failedToAbandonQuest": "Quest verlaten mislukt",
        "stats": {
          "questsCompleted": "Voltooide Quests",
          "totalXP": "Totaal XP",
          "streakDays": "Dag Reeks",
          "activeQuests": "Actieve Quests"
        }
      },

      // Authentication callbacks
      auth: {
        "twitch": {
          "processing": "Authenticeren met Twitch...",
          "processingDescription": "Wacht terwijl we je account verifiëren",
          "success": "Authenticatie Succesvol!",
          "successDescription": "Je wordt doorgestuurd naar je dashboard...",
          "error": "Authenticatie Mislukt",
          "errorDescription": "Je wordt teruggestuurd naar inloggen..."
        }
      },

      // Profile component
      profile: {
        "anonymousUser": "Anonieme Gebruiker",
        "memberSince": "Lid Sinds",
        "lastActive": "Laatst Actief",
        "emailStatus": "E-mail Status",
        "verified": "Geverifieerd",
        "unverified": "Niet Geverifieerd",
        "achievements": "Prestaties",
        "badges": {
          "newUser": "Nieuwe Gebruiker",
          "verified": "E-mail Geverifieerd"
        }
      },

      // Timeline component
      timeline: {
        "title": "Activiteit Tijdlijn",
        "recentActivity": "Recente Activiteit",
        "noActivity": "Geen recente activiteit om weer te geven",
        "events": {
          "accountCreated": "Account Aangemaakt",
          "profileUpdated": "Profiel Bijgewerkt",
          "questCompleted": "Quest Voltooid",
          "completedFirstQuest": "Je eerste quest voltooid!",
          "achievementUnlocked": "Prestatie Ontgrendeld",
          "newUserBadge": "Nieuwe Gebruiker badge verdiend"
        }
      },

      // Admin system
      admin: {
        "portal": "Admin Portaal",
        "accessDenied": "Toegang Geweigerd",
        "noPermission": "Je hebt geen toestemming om het admin portaal te betreden.",
        "pleaseLogin": "Log eerst in.",
        "checkingPrivileges": "Admin privileges controleren...",
        "overview": "Overzicht",
        "quests": "Quests",
        "questManagement": "Quest Beheer",
        "userManagement": "Gebruiker Beheer",
        "searchQuests": "Zoek quests...",
        "searchUsers": "Zoek gebruikers...",
        "createQuest": "Quest Maken",
        "createNewQuest": "Nieuwe Quest Maken",
        "editQuest": "Quest Bewerken",
        "deleteQuest": "Quest Verwijderen",
        "confirmDelete": "Weet je zeker dat je deze quest wilt verwijderen?",
        "selectQuest": "Selecteer een quest om details te bekijken",
        "loadingQuests": "Quests laden...",
        "loadingUsers": "Gebruikers laden...",
        "questCreated": "Quest succesvol gemaakt",
        "questUpdated": "Quest succesvol bijgewerkt",
        "questDeleted": "Quest succesvol verwijderd",
        "failedToCreate": "Quest maken mislukt",
        "failedToUpdate": "Quest bijwerken mislukt",
        "failedToDelete": "Quest verwijderen mislukt",
        "form": {
          "title": "Titel",
          "titlePlaceholder": "Voer quest titel in",
          "description": "Beschrijving",
          "descriptionPlaceholder": "Beschrijf de quest",
          "xpValue": "XP Waarde",
          "difficulty": "Moeilijkheid",
          "category": "Categorie",
          "categoryPlaceholder": "bijv. fitness, leren",
          "timeEstimate": "Tijd Schatting",
          "timeEstimatePlaceholder": "bijv. 30 min, 1 uur",
          "translations": "Vertalingen",
          "addTranslation": "Vertaling Toevoegen",
          "language": "Taal",
          "selectLanguage": "Selecteer taal",
          "saveChanges": "Wijzigingen Opslaan",
          "cancel": "Annuleren",
          "close": "Sluiten"
        },
        "status": {
          "active": "Actief",
          "inactive": "Inactief"
        },
        "stats": {
          "overviewAndStatistics": "Overzicht & Statistieken",
          "userStatistics": "Gebruiker Statistieken",
          "totalUsers": "Totaal Gebruikers",
          "activeUsers": "Actieve Gebruikers",
          "totalXPEarned": "Totaal XP Verdiend",
          "avgXPPerUser": "Gem. XP per Gebruiker",
          "questActivity": "Quest Activiteit",
          "questsCompleted": "Voltooide Quests",
          "questsFailed": "Mislukte Quests",
          "activeQuests": "Actieve Quests",
          "overallSuccessRate": "Algemeen Succespercentage",
          "availableQuests": "Beschikbare Quests",
          "totalQuests": "Totaal Quests",
          "inactiveQuests": "Inactieve Quests",
          "avgXPReward": "Gem. XP Beloning",
          "questBreakdown": "Quest Verdeling",
          "byDifficulty": "Op Moeilijkheid",
          "byCategory": "Op Categorie",
          "loadingStatistics": "Statistieken laden..."
        },
        "users": {
          "email": "E-mail",
          "basicInformation": "Basis Informatie",
          "username": "Gebruikersnaam",
          "created": "Aangemaakt",
          "lastActive": "Laatst Actief",
          "updated": "Bijgewerkt",
          "questStatistics": "Quest Statistieken",
          "completed": "Voltooid",
          "failed": "Mislukt",
          "active": "Actief",
          "totalXP": "Totaal XP",
          "successRate": "Succespercentage",
          "questHistoryPreview": "Quest Geschiedenis Voorbeeld",
          "noUsers": "Geen Gebruikers Gevonden",
          "tryDifferentSearch": "Probeer andere zoektermen",
          "noSelection": "Selecteer een gebruiker om details te bekijken",
          "notSet": "Niet ingesteld",
          "never": "Nooit",
          "anonymousUser": "Anonieme Gebruiker",
          "loadingUsers": "Gebruikers laden...",
          "searchByEmail": "Zoeken op e-mail...",
          "searchByUsername": "Zoeken op gebruikersnaam...",
          "searchByUID": "Zoeken op gebruikers-ID...",
          "searchType": "Zoektype",
          "searchTypeEmail": "E-mail",
          "searchTypeUsername": "Gebruikersnaam",
          "searchTypeUID": "Gebruikers-ID",
          "search": "Zoeken",
          "found": "gevonden",
          "userNotFound": "Gebruiker niet gevonden",
          "searchError": "Zoekfout",
          "noUsersFound": "Geen gebruikers gevonden",
          "tryDifferentEmail": "Probeer een ander e-mailadres",
          "searchPrompt": "Zoek een Gebruiker",
          "enterEmailToSearch": "Voer een e-mailadres in om een gebruiker te zoeken",
          "xpHistory": "XP Geschiedenis",
          "addXPEntry": "XP Entry Toevoegen",
          "noXPHistory": "Geen XP geschiedenis gevonden",
          "deleteXPEntry": "XP Entry Verwijderen",
          "confirmDeleteXP": "Weet je zeker dat je deze XP entry wilt verwijderen?",
          "addXPError": "XP entry toevoegen mislukt",
          "deleteXPError": "XP entry verwijderen mislukt",
          "xpEntryTitle": "Titel",
          "xpEntryTitlePlaceholder": "Voer XP entry titel in",
          "xpValue": "XP Waarde",
          "xpEntryType": "Type",
          "xpEntryStatus": "Status",
          "typeQuest": "Quest",
          "typeBonus": "Bonus",
          "typeAchievement": "Prestatie",
          "statusCompleted": "Voltooid",
          "statusFailed": "Mislukt",
          "statusActive": "Actief"
        },
        "questApproval": {
          "title": "Quest Goedkeuring",
          "pendingRequests": "Openstaande Verzoeken",
          "approvedRequests": "Goedgekeurde Verzoeken",
          "rejectedRequests": "Afgewezen Verzoeken",
          "noPendingRequests": "Geen openstaande quest verzoeken",
          "pending": "Openstaand",
          "approved": "Goedgekeurd",
          "rejected": "Afgewezen",
          "approveRequest": "Verzoek Goedkeuren",
          "rejectRequest": "Verzoek Afwijzen",
          "viewDetails": "Bekijk Details",
          "requestDetails": "Verzoek Details",
          "requestedBy": "Aangevraagd door",
          "requestedAt": "Aangevraagd op",
          "questTitle": "Quest Titel",
          "questDescription": "Quest Beschrijving",
          "questXP": "XP Waarde",
          "questDifficulty": "Moeilijkheid",
          "questCategory": "Categorie",
          "approvalMessage": "Goedkeuring Bericht",
          "rejectionMessage": "Afwijzing Bericht",
          "approvalMessagePlaceholder": "Optioneel bericht voor gebruiker",
          "rejectionMessagePlaceholder": "Reden voor afwijzing",
          "confirmApproval": "Weet je zeker dat je dit quest verzoek wilt goedkeuren?",
          "confirmRejection": "Weet je zeker dat je dit quest verzoek wilt afwijzen?",
          "requestApproved": "Verzoek succesvol goedgekeurd",
          "requestRejected": "Verzoek succesvol afgewezen",
          "failedToApprove": "Verzoek goedkeuren mislukt",
          "failedToReject": "Verzoek afwijzen mislukt",
          "loadingRequests": "Verzoeken laden..."
        }
      },

      // Quest system
      quests: {
        "available": "beschikbaar",
        "startQuest": "Start Quest",
        "abandon": "Quest Verlaten",
        "progress": "Voortgang",
        "onCompletion": "bij voltooiing",
        "description": "Beschrijving",
        "subquests": "Sub-quests",
        "difficulty": {
          "easy": "Makkelijk",
          "medium": "Gemiddeld",
          "hard": "Moeilijk"
        },
        "status": {
          "completed": "Voltooid",
          "failed": "Mislukt",
          "active": "Actief",
          "pending": "Wachtend op Goedkeuring",
          "approved": "Goedgekeurd",
          "rejected": "Afgewezen"
        },
        "pending": {
          "title": "Beschikbare Quests",
          "noPendingQuests": "Geen quests beschikbaar op dit moment",
          "watchTutorials": "Bekijk Tutorial Video's",
          "watchTutorialsDesc": "Voltooi de inleidende tutorial serie",
          "completeProfile": "Voltooi Je Profiel",
          "completeProfileDesc": "Vul alle vereiste profielinformatie in",
          "shareContent": "Deel Je Eerste Content",
          "shareContentDesc": "Maak en deel je eerste stuk content"
        },
        "active": {
          "title": "Actieve Quest",
          "inProgress": "Bezig",
          "noActiveQuest": "Geen Actieve Quest",
          "startNewQuest": "Start een nieuwe quest uit de beschikbare quests lijst",
          "verifyEmail": "Verifieer Je E-mail",
          "verifyEmailDesc": "Voltooi het e-mail verificatieproces",
          "started": "Gestart",
          "remaining": "resterend"
        },
        "steps": {
          "title": "Quest Stappen",
          "complete": "Voltooien",
          "checkEmail": "Controleer je e-mail inbox",
          "clickLink": "Klik op de verificatielink",
          "confirmVerification": "Bevestig verificatie"
        },
        "history": {
          "title": "Quest Geschiedenis",
          "all": "Alle",
          "completed": "Voltooid",
          "failed": "Mislukt",
          "noHistory": "Geen Quest Geschiedenis",
          "startCompleting": "Begin met het voltooien van quests om je geschiedenis hier te zien",
          "missed": "gemist",
          "firstLogin": "Eerste Login",
          "firstLoginDesc": "Succesvol voor de eerste keer ingelogd",
          "profileSetup": "Profiel Setup",
          "profileSetupDesc": "Initiële profiel setup voltooid",
          "socialShare": "Social Media Delen",
          "socialShareDesc": "Deel content op social media platforms",
          "weeklyChallenge": "Wekelijkse Uitdaging",
          "weeklyChallengeDesc": "Voltooi de wekelijkse uitdaging quest"
        }
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: process.env.NODE_ENV === 'development',
    
    interpolation: {
      escapeValue: false
    },
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage']
    }
  });

export default i18n; 